/**
 * Types and functions for working with 2d oriented bounding boxes (OBB2)
 * @module
 */
import { aabb2Center, aabb2Intersect, aabb2OverlapArea, aabb2Union, intervalsOverlap } from "./aabb2";
import type { AABB2 } from "./aabb2Types";
import { aabb2Height, aabb2New, aabb2Width } from "./aabb2Types";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "./core";
import { line2IsAxisAligned, line2IsHorizontal, line2IsVertical, line2ProjectPt } from "./line2";
import { polygonIntersection } from "./polyclip/polyClip";
import {
  polyLine2AABB2,
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2FromAABB2,
  polyLine2Rotate,
} from "./polyLine2";
import { polyLine2Close } from "./polyLine2";
import { polygonArea } from "./polyLine2Polygon";
import type { PolyLine2 } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import { vec2Dist, vec2Result, vec2RotateAround, type Vec2 } from "./vec2";

/**
 * Represents an oriented bounding box in 2D space
 */
export interface OBB2 {
  /** Center point of the OBB */
  centerX: number;
  centerY: number;
  /** Half-width and half-height of the OBB */
  halfWidth: number;
  halfHeight: number;
  /** Rotation angle in radians */
  rotation: number;
}

/**
 * Result of OBB collision detection
 */
export interface OBB2CollisionResult {
  /** Area of intersection (0 if not colliding) */
  intersectionArea: number;
  /** Intersection polygon */
  intersectionPolygon: PolyLine2;
  /** Intersection center */
  intersectionCenterX: number;
  intersectionCenterY: number;
}

/**
 * Creates a new OBB2 from center point, dimensions, and rotation
 */
export function obb2New(
  centerX: number,
  centerY: number,
  halfWidth: number,
  halfHeight: number,
  rotation: number = 0,
): OBB2 {
  return {
    centerX,
    centerY,
    halfWidth,
    halfHeight,
    rotation,
  };
}

/* 
  Creates a new OBB2 from an existing OBB2
*/
export function obb2Copy({ centerX, centerY, halfWidth, halfHeight, rotation }: OBB2): OBB2 {
  return obb2New(centerX, centerY, halfWidth, halfHeight, rotation);
}

/**
 * Creates an OBB2 from an AABB2 with optional rotation
 */
export function obb2FromAABB2(aabb: AABB2, rotation: number = 0): OBB2 {
  const [centerX, centerY] = [(aabb.minX + aabb.maxX) / 2, (aabb.minY + aabb.maxY) / 2];
  const halfWidth = (aabb.maxX - aabb.minX) / 2;
  const halfHeight = (aabb.maxY - aabb.minY) / 2;
  return obb2New(centerX, centerY, halfWidth, halfHeight, rotation);
}

/**
 * Checks if an OBB2 is axis aligned
 */
export function obb2IsAxisAligned(obb: OBB2): boolean {
  const axes = obb2GetAxes(obb);

  return line2IsAxisAligned(0, 0, axes[0][0], axes[0][1]);
}

/**
 * Checks if an OBB2 is horizontal
 */
export function obb2IsHorizontal(obb: OBB2): boolean {
  const axes = obb2GetAxes(obb);

  return line2IsHorizontal(0, 0, axes[0][0], axes[0][1]);
}

/**
 * Checks if an OBB2 is vertical
 */
export function obb2IsVertical(obb: OBB2): boolean {
  const axes = obb2GetAxes(obb);

  return line2IsVertical(0, 0, axes[0][0], axes[0][1]);
}

/**
 * Converts an OBB2 to an AABB2 by computing the axis-aligned bounding box
 * that contains the oriented bounding box
 */
export function aabb2FromOBB2(obb: OBB2): AABB2 {
  if (obb2IsHorizontal(obb)) {
    return aabb2New(
      obb.centerX - obb.halfWidth,
      obb.centerY - obb.halfHeight,
      obb.centerX + obb.halfWidth,
      obb.centerY + obb.halfHeight,
    );
  }

  if (obb2IsVertical(obb)) {
    return aabb2New(
      obb.centerX - obb.halfHeight,
      obb.centerY - obb.halfWidth,
      obb.centerX + obb.halfHeight,
      obb.centerY + obb.halfWidth,
    );
  }

  // get the permieter
  const corners = obb2GetCorners(obb);

  // get the aabb
  return polyLine2AABB2(corners);
}

/**
 * Gets the four corner points of an OBB2
 */
export function obb2GetCorners({ centerX, centerY, halfWidth, halfHeight, rotation }: OBB2): PolyLine2 {
  // get the unrotated perimeter
  const perimeter = polyLine2New([
    centerX - halfWidth,
    centerY - halfHeight,
    centerX + halfWidth,
    centerY - halfHeight,
    centerX + halfWidth,
    centerY + halfHeight,
    centerX - halfWidth,
    centerY + halfHeight,
  ]);

  // return the rotated perimeter
  return polyLine2Rotate(perimeter, rotation, centerX, centerY);
}

export function polyLine2FromOBB2(obb: OBB2): PolyLine2 {
  return polyLine2Close(obb2GetCorners(obb));
}

/**
 * Gets the axis vectors of an OBB2 (normalized direction vectors)
 */
export function obb2GetAxes({ rotation }: OBB2): [Vec2, Vec2] {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  return [
    [cos, sin], // X-axis
    [-sin, cos], // Y-axis
  ];
}

/**
 * Projects an OBB2 onto a given axis
 */
export function obb2ProjectOntoAxis(obb: OBB2, axisX: number, axisY: number): Vec2 {
  const corners = obb2GetCorners(obb);

  vec2Result[0] = Infinity;
  vec2Result[1] = -Infinity;

  for (let i = 0, l = corners.length; i < l; i += 2) {
    const x = corners[i];
    const y = corners[i + 1];

    const proj = x * axisX + y * axisY;
    vec2Result[0] = Math.min(vec2Result[0], proj);
    vec2Result[1] = Math.max(vec2Result[1], proj);
  }

  return vec2Result;
}

/**
 * Detects collision between two OBB2 objects and returns collision flag and intersection area
 * Uses precise polygon intersection calculation for accurate area measurement
 */
export function obb2CollisionDetect(a: OBB2, b: OBB2): OBB2CollisionResult | null {
  // First check if AABBs overlap for early exit
  const aabbA = aabb2FromOBB2(a);
  const aabbB = aabb2FromOBB2(b);

  if (!aabb2OverlapArea(aabbA, aabbB)) {
    return null;
  }

  // Fast path where both OBBs are axis aligned
  if (obb2IsAxisAligned(a) && obb2IsAxisAligned(b)) {
    const intersectionArea = aabb2OverlapArea(aabbA, aabbB);

    if (intersectionArea < DIST_EPSILON_SQUARED) {
      return null;
    }

    const intersectionAABB = aabb2Intersect(aabbA, aabbB);
    const [intersectionCenterX, intersectionCenterY] = aabb2Center(intersectionAABB);

    return {
      intersectionArea,
      intersectionPolygon: polyLine2FromAABB2(intersectionAABB),
      intersectionCenterX,
      intersectionCenterY,
    };
  }

  // Test all potential separating axes
  const axesA = obb2GetAxes(a);
  const axesB = obb2GetAxes(b);
  const allAxes = [...axesA, ...axesB];

  for (const [axisX, axisY] of allAxes) {
    // Project both OBBs onto this axis
    const [projAX, projAY] = obb2ProjectOntoAxis(a, axisX, axisY);
    const [projBX, projBY] = obb2ProjectOntoAxis(b, axisX, axisY);

    // Check for separation
    if (!intervalsOverlap(projAX, projAY, projBX, projBY)) {
      return null;
    }
  }

  // Get the polylines for both OBBs
  const plA = polyLine2FromOBB2(a);
  const plB = polyLine2FromOBB2(b);

  // Calculate precise intersection using polygon intersection
  // TODO: Find a faster way to do this (maybe using an approximation)
  const intersectionPolygons = polygonIntersection({ pls: [[plA], [plB]] });

  if (intersectionPolygons.length === 0) {
    return null;
  }

  // Calculate the total area of all intersection polygons
  const intersectionArea = polygonArea(intersectionPolygons[0]);
  if (intersectionArea < DIST_EPSILON_SQUARED) {
    return null;
  }

  const intersectionPolygon = intersectionPolygons[0][0];
  const intersectionPolygonCentroid = polyLine2Centroid(intersectionPolygon);

  if (!intersectionPolygonCentroid) {
    return null;
  }

  const [intersectionCenterX, intersectionCenterY] = intersectionPolygonCentroid;

  return {
    intersectionArea,
    intersectionPolygon,
    intersectionCenterX,
    intersectionCenterY,
  };
}

/**
 * Moves an OBB2 by the given offset
 */
export function obb2Move(obb: OBB2, dx: number, dy: number): OBB2 {
  obb.centerX += dx;
  obb.centerY += dy;

  return obb;
}

/**
 * Rotates an OBB2 around its center
 */
export function obb2RotateAroundCenter(obb: OBB2, rotationRad: number): OBB2 {
  obb.rotation += rotationRad;

  return obb;
}

/**
 * Rotates an OBB2 around a given point
 */
export function obb2Rotate(obb: OBB2, rotationRad: number, centerX: number = 0, centerY: number = 0): OBB2 {
  // rotate the OBB center
  [obb.centerX, obb.centerY] = vec2RotateAround(obb.centerX, obb.centerY, rotationRad, centerX, centerY);

  // rotate the OBB
  obb.rotation += rotationRad;

  return obb;
}

/**
 * Scales an OBB2 around its center
 */
export function obb2ScaleAroundCenter(obb: OBB2, scaleX: number, scaleY: number): OBB2 {
  obb.halfWidth *= scaleX;
  obb.halfHeight *= scaleY;

  return obb;
}

export function obb2ScaleAroundPoint(
  obb: OBB2,
  scaleX: number,
  scaleY: number,
  centerX: number,
  centerY: number,
): OBB2 {
  obb2ScaleAroundCenter(obb, scaleX, scaleY);
  obb.centerX = (obb.centerX - centerX) * scaleX + centerX;
  obb.centerY = (obb.centerY - centerY) * scaleY + centerY;

  return obb;
}

/**
 * Grows an OBB2 by the given amount
 */
export function obb2Grow(obb: OBB2, deltaX: number, deltaY: number): OBB2 {
  obb.halfWidth += deltaX;
  obb.halfHeight += deltaY;

  return obb;
}

export function obb2InSameOrientation(obb1: OBB2, obb2: OBB2, angleThreshold: number = 0.01): boolean {
  const angle1 = obb1.rotation;
  const angle2 = obb2.rotation;
  return Math.abs(angle1 - angle2) < angleThreshold;
}

export function obb2Sum(obb1: OBB2, obb2: OBB2, angleThreshold: number = 0.01): OBB2 | null {
  if (!obb2InSameOrientation(obb1, obb2, angleThreshold)) {
    return null;
  }

  const minX = Math.min(obb1.centerX - obb1.halfWidth, obb2.centerX - obb2.halfWidth);
  const minY = Math.min(obb1.centerY - obb1.halfHeight, obb2.centerY - obb2.halfHeight);
  const maxX = Math.max(obb1.centerX + obb1.halfWidth, obb2.centerX + obb2.halfWidth);
  const maxY = Math.max(obb1.centerY + obb1.halfHeight, obb2.centerY + obb2.halfHeight);

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const halfWidth = (maxX - minX) / 2;
  const halfHeight = (maxY - minY) / 2;

  return obb2New(centerX, centerY, halfWidth, halfHeight, obb1.rotation);
}

/**
 * Checks if a point is inside an OBB2
 */
export function obb2ContainsPoint(
  { centerX, centerY, halfWidth, halfHeight, rotation }: OBB2,
  x: number,
  y: number,
): boolean {
  // Transform point to OBB's local coordinate system
  const cos = Math.cos(-rotation);
  const sin = Math.sin(-rotation);

  const dx = x - centerX;
  const dy = y - centerY;

  const localX = dx * cos - dy * sin;
  const localY = dx * sin + dy * cos;

  return Math.abs(localX) <= halfWidth && Math.abs(localY) <= halfHeight;
}

/**
 * Calculates the area of an OBB2
 */
export function obb2Area({ halfWidth, halfHeight }: OBB2): number {
  return 4 * halfWidth * halfHeight;
}

/**
 * Creates an OBB2 from two points
 */
export function obb2ByTwoPoints(x1: number, y1: number, x2: number, y2: number, thickness: number = 0): OBB2 {
  // get the half size
  const halfWidth = vec2Dist(x1, y1, x2, y2) / 2;
  const halfHeight = thickness / 2;

  // get the center
  const centerX = (x1 + x2) / 2;
  const centerY = (y1 + y2) / 2;

  // get the rotation
  const rotationRad = Math.atan2(y2 - y1, x2 - x1);

  return obb2New(centerX, centerY, halfWidth, halfHeight, rotationRad);
}

/**
 * Creates an OBB2 from a thickened edge
 */
export function obb2ByThickenedEdge(
  pl: PolyLine2,
  edgeIndex: number,
  thickness: number,
  centerOnEdge: boolean = false,
): OBB2 {
  const [x1, y1] = polyLine2EdgeStart(pl, edgeIndex);
  const [x2, y2] = polyLine2EdgeEnd(pl, edgeIndex);

  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);

  if (len < DIST_EPSILON) {
    return obb2New(x1, y1, thickness / 2, thickness / 2, 0);
  }

  // get the U vector
  const ux = dx / len;
  const uy = dy / len;

  // get the V vector
  const vx = uy;
  const vy = -ux;

  // get the half width
  const halfWidth = vec2Dist(x1, y1, x2, y2) / 2;
  const halfHeight = thickness / 2;

  // get the center
  let centerX = (x1 + x2) / 2;
  let centerY = (y1 + y2) / 2;

  if (!centerOnEdge) {
    centerX += vx * halfHeight;
    centerY += vy * halfHeight;
  }

  // get the rotation
  const rotationRad = Math.atan2(uy, ux);

  return obb2New(centerX, centerY, halfWidth, halfHeight, rotationRad);
}

export function obb2FromPathAndPolyLines(
  path: PolyLine2,
  pls: PolyLine2[],
  centerOnPath: boolean = false,
): OBB2 | null {
  if (pls.length === 0) {
    return null;
  }

  const [startX, startY] = polyLine2EdgeStart(path, 0);
  const [endX, endY] = polyLine2EdgeEnd(path, 0);

  // calculate the rotation of the path
  const rotation = Math.atan2(endY - startY, endX - startX);

  // get the AABB of the polylines
  const aabb = pls.reduce(
    (acc, pl) => {
      const subAABB = polyLine2AABB2(pl);
      return aabb2Union(acc, subAABB);
    },
    aabb2New(Number.MAX_VALUE, Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE),
  );

  // project the aabb center into the path
  const [aabbCenterX, aabbCenterY] = aabb2Center(aabb);
  let [centerX, centerY] = line2ProjectPt(startX, startY, endX, endY, aabbCenterX, aabbCenterY);

  // remove rotation from the polylines
  const plsNoRotation = pls.map((pl) => {
    return polyLine2Rotate(polyLine2Copy(pl), -rotation, centerX, centerY);
  });

  // get the AABB of the no-rotation polylines
  const noRotationAABB = plsNoRotation.reduce(
    (acc, pl) => {
      const subAABB = polyLine2AABB2(pl);
      return aabb2Union(acc, subAABB);
    },
    aabb2New(Number.MAX_VALUE, Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE),
  );

  // build the OBB2
  const halfWidth = aabb2Width(noRotationAABB) / 2;
  const halfHeight = aabb2Height(noRotationAABB) / 2;

  // apply half height offset
  if (!centerOnPath) {
    const vx = -Math.sin(rotation);
    const vy = Math.cos(rotation);

    centerX += vx * halfHeight;
    centerY += vy * halfHeight;
  }

  return obb2New(centerX, centerY, halfWidth, halfHeight, rotation);
}

export function obb2HasNaN(obb: OBB2): boolean {
  return (
    isNaN(obb.centerX) || isNaN(obb.centerY) || isNaN(obb.halfWidth) || isNaN(obb.halfHeight) || isNaN(obb.rotation)
  );
}
